import { describeItemAffix, findNamedItem, resolveItemFlavor, resolveItemModelId, resolveUpgradeStats, type Equipment, type NamedItemDefinition } from '@sorrem/shared';
import { useState, type ReactNode } from 'react';
import type { UIInventoryItem } from '@sorrem-client/types';
import { Button, Icon } from '@sorrem/ui';
import { ItemIcon } from '@sorrem-client/ui/components/ItemIcon';
import { toEquipment } from '@sorrem-client/ui/shared/inventory-item';
import { buildItemCardStats, formatGearRequirement, formatGearWeakening, formatItemKind, UPGRADE_STAT_CARD_KEYS, type ItemCardStat } from '@sorrem-client/ui/shared/item-card-stats';
import classNames from 'classnames';
import styles from './ItemCard.module.scss';

interface ItemCardProps {
    item: UIInventoryItem;
    /**
     * The equipped items its stats compare against (`resolveComparedEquipment`):
     * what it would swap out, or both hands' items, main then off (#341). None
     * when left out.
     */
    compared?: readonly Equipment[];
    /** Equips the item; omitted when it can't be equipped. */
    onEquip?: () => void;
    /** Drops the item on the ground (in a run); omitted elsewhere. */
    onDrop?: () => void;
    /** Drawn in the art box in place of the item's icon, e.g. its 3D model (Crafting, #287). */
    art?: ReactNode;
    /**
     * `workbench` (Crafting's comparison, #287): a half again larger art box
     * beside the name, kind and stats, with the affixes and flavor across the
     * card's full width below, no "Compared with" line, and every stat an
     * upgrade raises, even one at 0.
     */
    variant?: 'carried' | 'workbench';
    /** The named items the card reads its behavior from; the game's catalog when left out (the Named Item Designer passes its drafts). */
    namedCatalog?: readonly NamedItemDefinition[];
    /** Drawn at the end of each affix line, e.g. Crafting's reroll link (#287). */
    affixAction?: (index: number) => ReactNode;
    /** Flashes each stat green or red as it rises or falls, e.g. as Crafting's chosen upgrade level changes (#287). */
    flashChanges?: boolean;
}

const NONE_COMPARED: readonly Equipment[] = [];

/** One stat row: its value flashes as it changes, when asked to, the way core stats do (`StatRow`). */
function StatLine({ stat, flashChanges }: { readonly stat: ItemCardStat; readonly flashChanges: boolean }): JSX.Element {
    const shown = Number(stat.value);
    // The value last seen and how it last moved; each move replays the flash (a new key restarts it).
    const [change, setChange] = useState<{ value: number; count: number; direction: 'increase' | 'decrease' | null }>(
        { value: shown, count: 0, direction: null },
    );
    if (flashChanges && change.value !== shown) {
        setChange({ value: shown, count: change.count + 1, direction: shown > change.value ? 'increase' : 'decrease' });
    }
    return (
        <div className={styles.stat}>
            <dt>{stat.label}</dt>
            <dd
                key={change.count}
                className={classNames(styles.value, change.direction === 'increase' && styles.increaseFlash, change.direction === 'decrease' && styles.decreaseFlash)}
            >
                {stat.value}
            </dd>
            {stat.deltas.length === 0
                ? <dd className={styles.delta} />
                : stat.deltas.map((entry, index) => (
                    <dd key={index} className={classNames(styles.delta, entry.delta > 0 && styles.better, entry.delta < 0 && styles.worse)}>
                        {entry.delta > 0 && <Icon icon='arrow-up' size='small' />}
                        {entry.delta < 0 && <Icon icon='arrow-down' size='small' />}
                        {entry.text}
                    </dd>
                ))}
        </div>
    );
}

/** What the card says it is compared with, e.g. "Compared with Shortsword (main hand) and Buckler (off hand)". */
function formatCompared(compared: readonly Equipment[]): string | undefined {
    const [first, second] = compared;
    if (first === undefined) {
        return undefined;
    }
    return second === undefined
        ? `Compared with ${first.name}`
        : `Compared with ${first.name} (main hand) and ${second.name} (off hand)`;
}

/** An item's name with its upgrade level, e.g. "Shortsword +2". */
function formatItemName(item: UIInventoryItem): string {
    const upgradeLevel = item.instance?.upgradeLevel ?? 0;
    return upgradeLevel > 0 ? `${item.name} +${upgradeLevel}` : item.name;
}

/** A carried item's name, kind and stats against what it would replace, with an Equip action. */
export function ItemCard({ item, compared = NONE_COMPARED, onEquip, onDrop, art, variant = 'carried', affixAction, flashChanges = false, namedCatalog }: ItemCardProps): JSX.Element {
    // The workbench is where items are upgraded: it shows what an upgrade raises even before it has any.
    const upgradeStats = variant === 'workbench' ? resolveUpgradeStats(toEquipment(item)).map((entry) => UPGRADE_STAT_CARD_KEYS[entry.stat]) : [];
    const stats = buildItemCardStats(item, compared, upgradeStats);
    // Against both hands, each delta column is headed by its hand (#341).
    const isBothHands = compared.length === 2;
    // What the Veil remembered with it: picked from its affixes, the same line every time for this copy.
    const flavor = resolveItemFlavor(item);
    // A named item (Unforgotten, Empyreal) adds what its unique behavior does.
    const named = findNamedItem(item.instance?.namedItemId, namedCatalog);
    const affixes = item.instance?.affixes ?? [];
    // The workbench's cards sit side by side, so saying what one is compared with is redundant there.
    const comparedLine = variant === 'carried' ? formatCompared(compared) : undefined;
    const hasDetails = affixes.length > 0 || named !== undefined || flavor !== undefined || comparedLine !== undefined;

    return (
        <section className={classNames(styles.itemCard, variant === 'workbench' && styles.workbench)} aria-label={item.name}>
            <div className={styles.art} aria-hidden='true'>
                {art ?? <ItemIcon modelId={resolveItemModelId(item)} />}
            </div>
            <h3 className={classNames(styles.name, styles[item.rarity])}>{formatItemName(item)}</h3>
            <div className={styles.summary}>
                <p className={styles.kind}>{formatItemKind(item)}</p>
                {/* Gear below its requirement is weakened, never locked (#250): the card says by how much. */}
                {item.requirement !== undefined && (
                    <p className={classNames(styles.requirement, item.potency !== undefined && styles.unmet)}>{formatGearRequirement(item.requirement)}</p>
                )}
                {item.potency !== undefined && <p className={styles.weakened}>{formatGearWeakening(item.potency)}</p>}
            </div>
            {stats.length > 0 && (
                <div className={classNames(styles.stats, isBothHands && styles.bothHands)}>
                    {isBothHands && (
                        <div className={classNames(styles.stat, styles.handHeads)} aria-hidden='true'>
                            <span className={styles.handHead}>Main</span>
                            <span className={styles.handHead}>Off</span>
                        </div>
                    )}
                    <dl className={styles.statList}>
                        {stats.map((stat) => <StatLine key={stat.key} stat={stat} flashChanges={flashChanges} />)}
                    </dl>
                </div>
            )}
            {hasDetails && (
                <div className={styles.details}>
                    {affixes.length > 0 && (
                        <ul className={styles.affixes} aria-label='Affixes'>
                            {affixes.map((affix, index) => (
                                <li key={`${affix.affixId}-${index}`}>
                                    <span>{describeItemAffix(affix)}</span>
                                    {affixAction?.(index)}
                                </li>
                            ))}
                        </ul>
                    )}
                    {named !== undefined && (
                        <>
                            <p className={classNames(styles.behavior, styles[named.rarity])}>{named.description}</p>
                            <p className={styles.whole}>It can't be upgraded or rerolled.</p>
                        </>
                    )}
                    {flavor !== undefined && <p className={styles.flavor}>{flavor.text}</p>}
                    {comparedLine !== undefined && <p className={styles.compared}>{comparedLine}</p>}
                </div>
            )}
            {(onEquip !== undefined || onDrop !== undefined) && (
                <div className={styles.actions}>
                    {onEquip !== undefined && <Button onClick={onEquip}>Equip</Button>}
                    {onDrop !== undefined && <Button variant='secondary' onClick={onDrop}>Drop</Button>}
                </div>
            )}
        </section>
    );
}
